import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from "@/components/ui/input";
import { UserPlus, Chrome } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !email || !password) {
      toast.error('All fields required');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    setLoading(true);
    try {
      await register(username, email, password);
      toast.success('Welcome to WarSyndicate, soldier!');
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="register-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="ws-logo text-3xl">
            <span className="ws-logo-war">WAR</span>
            <span className="ws-logo-syndicate">SYNDICATE</span>
          </h1>
          <p className="text-[#666] text-xs mt-2">JOIN THE SYNDICATE</p>
        </div>

        <div className="bg-[#111] border border-[#333] p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-[#666] uppercase mb-2 block">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                className="bg-black border-[#333] text-[#44ff44] placeholder:text-[#444] focus:border-[#cc0000]"
                data-testid="register-username"
              />
            </div>

            <div>
              <label className="text-xs text-[#666] uppercase mb-2 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-black border-[#333] text-[#44ff44] placeholder:text-[#444] focus:border-[#cc0000]"
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="text-xs text-[#666] uppercase mb-2 block">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-black border-[#333] text-[#44ff44] placeholder:text-[#444] focus:border-[#cc0000]"
                data-testid="register-password"
              />
            </div>

            <div>
              <label className="text-xs text-[#666] uppercase mb-2 block">Confirm Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-black border-[#333] text-[#44ff44] placeholder:text-[#444] focus:border-[#cc0000]"
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-2.5 flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              <UserPlus size={16} />
              {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#333]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#111] px-2 text-[#666]">OR</span>
            </div>
          </div>

          <button
            onClick={loginWithGoogle}
            className="w-full btn-secondary py-2.5 flex items-center justify-center gap-2"
            data-testid="register-google"
          >
            <Chrome size={16} />
            CONTINUE WITH GOOGLE
          </button>

          <p className="text-center text-xs text-[#666] mt-6">
            Already a member?{' '}
            <Link to="/login" className="text-[#ffcc00] hover:text-[#ffd700]">
              LOGIN
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
